import { useEffect, useState } from 'react';
import { Icon } from './Icon.jsx';
import { usePdf } from '../store/pdfStore.js';
import { useConfig } from '../store/configStore.js';
import { useT } from '../i18n/index.js';
import { FONT_FAMILIES } from '../utils/pdfOps.js';

const DRAW_SHAPES = [
  { key: 'free', icon: 'pen' },
  { key: 'line', icon: 'line' },
  { key: 'rect', icon: 'square' },
  { key: 'fillrect', icon: 'squareFill' },
  { key: 'ellipse', icon: 'ellipse' },
  { key: 'circle', icon: 'circle' },
];

export function ToolsBar({ onOpenSignature, onExport, onAddPdf, onSplit, onPrev, onNext, busy }) {
  const t = useT();
  const tool = usePdf((s) => s.tool);
  const setTool = usePdf((s) => s.setTool);
  const view = usePdf((s) => s.view);
  const goAll = usePdf((s) => s.goAll);
  const goGrid = usePdf((s) => s.goGrid);
  const pendingSig = usePdf((s) => s.pendingSignature);
  const undo = usePdf((s) => s.undo);
  const redo = usePdf((s) => s.redo);
  const past = usePdf((s) => s.past);
  const future = usePdf((s) => s.future);
  const pages = usePdf((s) => s.pages);
  const pagesLen = pages.length;
  const currentPageId = usePdf((s) => s.currentPageId);
  const curIdx = pages.findIndex((p) => p.id === currentPageId);
  const safeIdx = curIdx < 0 ? 0 : curIdx;
  const textSelection = usePdf((s) => s.textSelection);
  const annotations = usePdf((s) => s.annotations);
  const clearTextSelection = usePdf((s) => s.clearTextSelection);
  const deleteTextSelection = usePdf((s) => s.deleteTextSelection);
  const highlightTextSelection = usePdf((s) => s.highlightTextSelection);
  const restyleTextSelection = usePdf((s) => s.restyleTextSelection);

  const editor = useConfig((s) => s.config.settings.editor);
  const setPath = useConfig((s) => s.setPath);
  const setEditor = (key, val) => setPath(`settings.editor.${key}`, val);

  const handleSign = () => {
    if (pendingSig) setTool('sign');
    else onOpenSignature();
  };

  const hasTextSel = textSelection.size > 0;
  const drawShape = editor.drawShape || 'free';

  // Style state shown in the text-selection bar (drives the B/I/U/S
  // active states and the font/size/color inputs).
  const [selStyle, setSelStyle] = useState({
    font: editor.defaultFont,
    size: editor.defaultFontSize,
    color: editor.defaultColor,
    bold: false,
    italic: false,
    underline: false,
    strike: false,
  });

  // Seed the bar from the first selected glyph whenever the selection
  // changes: reuse an existing override's style if there is one,
  // otherwise inherit the glyph's own font + size so toggling a single
  // attribute never resets the others.
  useEffect(() => {
    if (textSelection.size === 0) return;
    const first = textSelection.values().next().value;
    if (!first) return;
    const list = annotations[first.pageId] || [];
    const existing = list.find(
      (a) => a.type === 'text' && a.originalItemKey === first.itemKey
    );
    if (existing) {
      setSelStyle({
        font: existing.font || editor.defaultFont,
        size: Math.round(existing.size || first.fontSize || editor.defaultFontSize),
        color: existing.color || '#111111',
        bold: !!existing.bold,
        italic: !!existing.italic,
        underline: !!existing.underline,
        strike: !!existing.strike,
      });
    } else {
      setSelStyle({
        font: first.font || editor.defaultFont,
        size: Math.round(first.fontSize || editor.defaultFontSize),
        color: '#111111',
        bold: false,
        italic: false,
        underline: false,
        strike: false,
      });
    }
  }, [textSelection, annotations, editor.defaultFont, editor.defaultFontSize]);

  // Apply only the changed attribute; the store merges it into the
  // existing override (or seeds a new one from the glyph) so the rest
  // of the original styling is preserved.
  const applyStyle = (patch) => {
    setSelStyle((prev) => ({ ...prev, ...patch }));
    restyleTextSelection(patch);
  };

  return (
    <div className="tools-bar">
      {/* Row 1 — navigation & document actions */}
      <div className="tools-row">
        <div className="seg">
          <button className={`btn sm ${view === 'all' ? 'primary' : ''}`} onClick={goAll}>
            <Icon name="doc" size={14} /> {t('view.continuous')}
          </button>
          <button className={`btn sm ${view === 'grid' ? 'primary' : ''}`} onClick={goGrid}>
            <Icon name="selectAll" size={14} /> {t('view.grid')}
          </button>
        </div>

        <span className="divider" />

        <div className="seg">
          <button
            className="btn icon sm"
            onClick={onPrev}
            disabled={!pagesLen || safeIdx <= 0}
            title={`${t('editor.prev')} · ←`}
            aria-label={t('editor.prev')}
          >
            <Icon name="chevLeft" size={14} />
          </button>
          <span className="chip" style={{ alignSelf: 'center' }}>
            {pagesLen ? t('editor.page_indicator', { i: safeIdx + 1, n: pagesLen }) : '—'}
          </span>
          <button
            className="btn icon sm"
            onClick={onNext}
            disabled={!pagesLen || safeIdx >= pagesLen - 1}
            title={`${t('editor.next')} · →`}
            aria-label={t('editor.next')}
          >
            <Icon name="chevRight" size={14} />
          </button>
        </div>

        <span className="divider" />

        <div className="seg">
          <button
            className="btn icon sm"
            onClick={undo}
            disabled={!past.length}
            title={`${t('toolbar.undo')} · Ctrl+Z`}
            aria-label={t('toolbar.undo')}
          >
            <Icon name="undo" size={15} />
          </button>
          <button
            className="btn icon sm"
            onClick={redo}
            disabled={!future.length}
            title={`${t('toolbar.redo')} · Ctrl+Y`}
            aria-label={t('toolbar.redo')}
          >
            <Icon name="redo" size={15} />
          </button>
        </div>

        <span style={{ marginLeft: 'auto' }} />

        <button className="btn sm" onClick={onAddPdf} title={t('toolbar.add_pdf')}>
          <Icon name="merge" size={14} /> {t('toolbar.add_pdf')}
        </button>
        <button
          className="btn sm"
          onClick={onSplit}
          disabled={!pagesLen}
          title={t('toolbar.split')}
        >
          <Icon name="scissors" size={14} /> {t('toolbar.split')}
        </button>
        <button className="btn sm primary" onClick={onExport} disabled={!pagesLen || busy}>
          <Icon name="download" size={14} /> {t('app.save_copy')}
        </button>
      </div>

      {/* Row 2 — editing tools & their options */}
      <div className="tools-row">
        <ToolBtn icon="select" name="select" current={tool} onClick={() => setTool('select')} label={t('tools.select')} />
        <ToolBtn icon="text" name="text" current={tool} onClick={() => setTool('text')} label={t('tools.text')} />
        <ToolBtn icon="sign" name="sign" current={tool} onClick={handleSign} label={t('tools.sign')} />
        <ToolBtn icon="pen" name="draw" current={tool} onClick={() => setTool('draw')} label={t('tools.draw')} />

      {tool === 'text' && (
        <>
          <span className="divider" />
          <select className="btn sm" value={editor.defaultFont} onChange={(e) => setEditor('defaultFont', e.target.value)} title={t('editor.font')} aria-label={t('editor.font')}>
            {FONT_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <input type="number" min="6" max="96" step="1" className="btn sm size-input"
            value={editor.defaultFontSize}
            onChange={(e) => setEditor('defaultFontSize', parseInt(e.target.value || '14', 10))}
            title={t('editor.size')} aria-label={t('editor.size')} />
          <input type="color" className="color-input"
            value={editor.defaultColor}
            onChange={(e) => setEditor('defaultColor', e.target.value)}
            title={t('editor.color')} aria-label={t('editor.color')} />
          <button className={`btn sm ${editor.defaultBold ? 'primary' : ''}`} onClick={() => setEditor('defaultBold', !editor.defaultBold)} title={t('editor.bold')} style={{ fontWeight: 800 }}>B</button>
          <button className={`btn sm ${editor.defaultItalic ? 'primary' : ''}`} onClick={() => setEditor('defaultItalic', !editor.defaultItalic)} title={t('editor.italic')} style={{ fontStyle: 'italic' }}>I</button>
          <button className={`btn sm ${editor.defaultUnderline ? 'primary' : ''}`} onClick={() => setEditor('defaultUnderline', !editor.defaultUnderline)} title={t('editor.underline')} style={{ textDecoration: 'underline' }}>U</button>
          <button className={`btn sm ${editor.defaultStrike ? 'primary' : ''}`} onClick={() => setEditor('defaultStrike', !editor.defaultStrike)} title={t('editor.strike')} style={{ textDecoration: 'line-through' }}>S</button>
          <span className="tiny muted">{t('editor.highlight_color')}</span>
          <input type="color" className="color-input"
            value={editor.highlightColor}
            onChange={(e) => setEditor('highlightColor', e.target.value)}
            title={t('editor.highlight_color')} aria-label={t('editor.highlight_color')} />
        </>
      )}

      {tool === 'draw' && (
        <>
          <span className="divider" />
          <div className="seg">
            {DRAW_SHAPES.map((s) => (
              <button
                key={s.key}
                className={`btn icon sm ${drawShape === s.key ? 'primary' : ''}`}
                onClick={() => setEditor('drawShape', s.key)}
                title={t(`draw.${s.key}`)}
                aria-label={t(`draw.${s.key}`)}
              >
                <Icon name={s.icon} size={14} />
              </button>
            ))}
          </div>
          <input type="color" className="color-input"
            value={editor.shapeColor}
            onChange={(e) => setEditor('shapeColor', e.target.value)}
            title={t('editor.color')} aria-label={t('editor.color')} />
          <input type="range" min="1" max="12" step="1"
            value={editor.strokeWidth}
            onChange={(e) => setEditor('strokeWidth', parseInt(e.target.value, 10))}
            title={t('editor.stroke')} aria-label={t('editor.stroke')} />
          <span className="tiny muted">{editor.strokeWidth}px</span>
        </>
      )}
      </div>

      {hasTextSel && (
        <div className="text-sel-bar">
          <span className="chip">{t('selection.count', { n: textSelection.size })}</span>

          <select
            className="btn sm"
            value={selStyle.font}
            onChange={(e) => applyStyle({ font: e.target.value })}
            title={t('editor.font')}
            aria-label={t('editor.font')}
          >
            {FONT_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <input
            type="number" min="6" max="96" step="1"
            className="btn sm size-input"
            value={selStyle.size}
            onChange={(e) => applyStyle({ size: parseInt(e.target.value || '14', 10) })}
            title={t('editor.size')}
            aria-label={t('editor.size')}
          />
          <input
            type="color" className="color-input"
            value={selStyle.color}
            onChange={(e) => applyStyle({ color: e.target.value })}
            title={t('editor.color')}
            aria-label={t('editor.color')}
          />
          <button className={`btn sm ${selStyle.bold ? 'primary' : ''}`} onClick={() => applyStyle({ bold: !selStyle.bold })} title={t('editor.bold')} style={{ fontWeight: 800 }}>B</button>
          <button className={`btn sm ${selStyle.italic ? 'primary' : ''}`} onClick={() => applyStyle({ italic: !selStyle.italic })} title={t('editor.italic')} style={{ fontStyle: 'italic' }}>I</button>
          <button className={`btn sm ${selStyle.underline ? 'primary' : ''}`} onClick={() => applyStyle({ underline: !selStyle.underline })} title={t('editor.underline')} style={{ textDecoration: 'underline' }}>U</button>
          <button className={`btn sm ${selStyle.strike ? 'primary' : ''}`} onClick={() => applyStyle({ strike: !selStyle.strike })} title={t('editor.strike')} style={{ textDecoration: 'line-through' }}>S</button>

          <span className="divider" />

          <button className="btn sm" onClick={() => highlightTextSelection(editor.highlightColor)}>
            <Icon name="hilite" size={14} /> {t('selection.highlight')}
          </button>
          <input
            type="color" className="color-input"
            value={editor.highlightColor}
            onChange={(e) => setEditor('highlightColor', e.target.value)}
            title={t('editor.highlight_color')}
            aria-label={t('editor.highlight_color')}
          />
          <button className="btn sm danger" onClick={deleteTextSelection}>
            <Icon name="trash" size={14} /> {t('selection.delete')}
          </button>
          <button className="btn sm ghost" onClick={clearTextSelection}>
            <Icon name="close" size={14} /> {t('selection.clear')}
          </button>
        </div>
      )}
    </div>
  );
}

function ToolBtn({ icon, name, current, onClick, label }) {
  return (
    <button className={`btn sm tool-btn ${current === name ? 'primary' : ''}`} onClick={onClick} title={label}>
      <Icon name={icon} size={14} /> <span className="tool-label">{label}</span>
    </button>
  );
}
